import { useState, useEffect, useRef } from "react";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { PrivateStackParamList } from "@app/navigationApp/types";
import ROUTE from "@app/navigationApp/routes";
import { isFromAndroidWebview, isIframeCheck, REPAYMENT_COLLECTION_TYPE, trackEvent } from "@app/utils";
import InAppBrowser from "react-native-inappbrowser-reborn";
import BackgroundTimer from "react-native-background-timer";
import axios from "axios";
import { Platform } from "react-native";
import { endpoints } from "@app/api/apis";
import { BUILD_TYPE } from "@voltmoney/micro-frontend";
import { PaymentStatuses } from "@app/screens/ConfirmPay/ConfirmPay";
import { useAppSelector } from "@app/store/hooks";

type NavigationProp = NativeStackNavigationProp<PrivateStackParamList>;

interface BajajPaymentParams {
    selectedAmount: number;
    creditId: string;
    paymentType: string;
    isDecreaseForeclosureFlow: boolean;
    navigation: NavigationProp;
    setOpenInProgressModal: (isOpen: boolean) => void;
}

const useBajajPayment = () => {
    const [loading, setLoading] = useState(false);
    let polling = useRef<NodeJS.Timeout | null>(null);
    const {show_bajaj_pg,show_bajaj_pg_interest,bfl_partner_repayment} = useAppSelector((state) => state.firebaseParams);
    const platformCode = useAppSelector((state) => state.platformParams);
    const startBajajPayment = async ({
        selectedAmount,
        creditId,
        paymentType,
        isDecreaseForeclosureFlow,
        navigation,
        setOpenInProgressModal,
    }: BajajPaymentParams): Promise<void> => {
        setLoading(true);
        let popupWindow: Window | null = null;
        let showBajajPg =  paymentType === REPAYMENT_COLLECTION_TYPE.IN_APP_INTEREST_COLLECTION ? show_bajaj_pg_interest : show_bajaj_pg;
        let enableBajajPG = false;
        if(platformCode === undefined || platformCode === null){
            enableBajajPG = true;
        }
        else {
            enableBajajPG = bfl_partner_repayment;
        }
        let isPaymentModeBajaj = enableBajajPG && (showBajajPg === true || showBajajPg === "true" )

        const payload = {
            baseOriginUrl: window?.location?.href,
            collectionAmount: `${selectedAmount}`,
            creditId: creditId,
            paymentGatewayType: isPaymentModeBajaj ? "BAJAJ" : null,
            redirect: Platform.OS === "web",
            collectionType: paymentType,
            isDecreaseForeclosureFlow: isDecreaseForeclosureFlow,
        };

        try {
            const response = await axios.post(endpoints.paymentInit, payload);
            if (response?.status === 200) {
                if (Platform.OS === 'web') {
                    if ((isIframeCheck() || isFromAndroidWebview()) && !isPaymentModeBajaj) {
                        setOpenInProgressModal(true);
                        const url = response?.data?.paymentLink;
                        const width = 1002;
                        const height = 704;
          
                        // Calculate the position to center the popup on the screen
                        const left = (window.innerWidth - width) / 2;
                        const top = (window.innerHeight - height) / 2;
                        let popupWindow: Window | null = null;
                        
                        if (isFromAndroidWebview()) {
                            popupWindow = window.open(url, "_blank");
                        } else {
                            popupWindow = window.open(url, "_blank", "width=" + width + ", height=" + height + ", top=" + top + ", left=" + left);
                        }
                        
                        // Set up polling for payment status
                        if (response?.data?.collectionId) {
                            polling.current = setInterval(() => {
                                pollBajajPayment(response?.data?.collectionId, navigation, popupWindow);
                            }, 5000);
                            // Clear polling after 3 minutes (180000 ms)
                            setTimeout(() => {
                                if (polling.current) {
                                    clearInterval(polling.current);
                                    polling.current = null;
                                }
                            }, 180000);
                        }
                    } else {
                        const buildType = BUILD_TYPE.BORROWER_PRODUCTION
                        if(isPaymentModeBajaj){
                          if(isIframeCheck()){
                            const form = document.createElement('form');
                            form.method = 'POST';
                            form.action= buildType === BUILD_TYPE.BORROWER_PRODUCTION ? ' https://payment.bajajfinserv.in/Paymentgateway/Payments/Direct_Loan_Payment'
                            : 'https://uatpayments.bajajfinserv.in/Paymentgateway/Payments/Direct_Loan_Payment';                                                       
                            form.target = 'paymentPopup'; 
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = 'msg';
                            input.value = response.data.paymentLink;
                             popupWindow = window.open('', 'paymentPopup', 'width=600,height=600');
      
                            // Append input to form
                            form.appendChild(input);
                             document.body.appendChild(form);
                            form.submit()
                          } else {
                            const form = document.createElement('form');
                            form.method = 'POST';
                            form.action = buildType === BUILD_TYPE.BORROWER_PRODUCTION ? ' https://payment.bajajfinserv.in/Paymentgateway/Payments/Direct_Loan_Payment'
                            : 'https://uatpayments.bajajfinserv.in/Paymentgateway/Payments/Direct_Loan_Payment';
                            const input = document.createElement('input');
                            input.type = 'hidden';
                            input.name = 'msg';
                            input.value = response.data.paymentLink;
                            form.appendChild(input);
                            document.body.appendChild(form);
                            form.submit()
                          }
                        } else {
                            window.open(response.data.paymentLink, "_self")
                          }
                    }
                }
                else {
                    navigation.navigate(ROUTE.PAYMENT_WEBVIEW, {
                        uri: response?.data?.paymentLink,
                        collectionId: response?.data?.collectionId,
                        doFormPost:true,
                    });
                    const url = endpoints.paymentPolling + response?.data?.collectionId;
                    BackgroundTimer.runBackgroundTimer(async () => {
                        axios.get(url)
                            .then((response) => {
                            if (response.data.collectionStatus !== 'REQUESTED') {
                                InAppBrowser.close()
                                BackgroundTimer.stopBackgroundTimer()
                                navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId: response.data.collectionId });
                            }
                        })
                    },3000);
                }
            }
        } catch (error) {
            console.error('Bajaj Payment Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const stopPolling = () => {
        if (polling.current) {
            clearInterval(polling.current);
            polling.current = null; // Reset polling to null after clearing
        }
    };
    
    const pollBajajPayment = (
        collectionId: string, 
        navigation: NavigationProp, 
        popupWindow: Window | null, 
    ): void => {
        
        const url = endpoints.paymentPolling + collectionId;
        axios.get(url)
            .then((response) => {
                if (popupWindow?.closed) {
                    stopPolling();
                } else {
                    if (response.data.collectionStatus === 'COLLECTION_COMPLETE' || 
                        response.data.collectionStatus === 'CANCELLED' || 
                        response.data.collectionStatus === 'FAILED') {
                        stopPolling();
                        if (popupWindow) {
                            popupWindow.close();
                        }
                        
                        // Track events based on payment status
                        if (Platform.OS === 'ios' || Platform.OS === 'android') {
                            if (response.data.collectionStatus === PaymentStatuses.COLLECTION_COMPLETE || 
                                response.data.collectionStatus === PaymentStatuses.SETTLED || 
                                response.data.collectionStatus === PaymentStatuses.SUCCESS_WITH_LENDER_RECON_FAILED) {
                                trackEvent("BAJAJ_PAYMENT_COMPLETED", { collectionStatus: response.data.collectionStatus });
                            } else if (response.data.collectionStatus === PaymentStatuses.FAILED) {
                                trackEvent("BAJAJ_PAYMENT_FAILED", { collectionStatus: response.data.collectionStatus });
                            } else if (response.data.collectionStatus === PaymentStatuses.CANCELLED) {
                                trackEvent("BAJAJ_PAYMENT_CANCELLED", { collectionStatus: response.data.collectionStatus });
                            } else if (response.data.collectionStatus === PaymentStatuses.PENDING_SETTLEMENT) {
                                trackEvent("BAJAJ_PAYMENT_PENDING_SETTLEMENT", { collectionStatus: response.data.collectionStatus });
                            } else {
                                trackEvent("BAJAJ_PAYMENT_GOES_WRONG", { collectionStatus: response.data.collectionStatus });
                            }
                        }
                        
                        navigation.navigate(ROUTE.PAYMENT_STATUS, { collectionId: response.data.collectionId });
                    }
                }
            })
            .catch((error) => {
                console.error('Bajaj Payment Polling Error:', error);
            });
    };

    useEffect(() => {
        return () => {
            stopPolling(); // Clear polling on component unmount
        };
    }, []);

    return { startBajajPayment, loading };
};

export default useBajajPayment;
